/* mg-pdp.js: product page helpers for §89 (assets/mg-pdp.js, loaded deferred by the mg_lead block).
   Safe to load more than once; binds per section and re-binds after the theme replaces the media
   (variant media refresh) or the section (theme editor). */
(() => {
  const DESK = matchMedia('(min-width: 990px)');
  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)');
  const PAD = 24;

  const q = (root, sel) => root.querySelector(sel);
  const items = sec => [...sec.querySelectorAll('.product__media-list-container > .product__media-item:not(.swiper-slide-duplicate)')];
  const mediaIdOf = item => (item.dataset.mediaId || '').split('-').pop();

  /* 1. Sticky rail (990+). When the rail is taller than the window it scrolls with the page until
        its far edge shows, then holds; scrolling back reverses that. It clears a revealed header and
        the sticky add bar. Content that grows (an accordion, Show more) grows downward: if the rail
        is resting on the end of the gallery, the gallery column gets the same runway underneath, so
        the row the shopper clicked never moves. */
  function initRail(sec) {
    const rail = q(sec, '.product__info-container');
    if (!rail || rail.__mgRail) return;
    rail.__mgRail = true;
    const header = document.querySelector('.shopify-section-header');
    let t = null, lastY = scrollY, hdrShown = false, raf = 0, timer = 0;
    let runway = 0, recTop = null, recY = 0, forcedBar = false;
    const media = () => q(sec, '.product__outer > .product__media');
    const bar = () => q(sec, '.product-sticky-add-bar');
    const headerH = () => {
      if (!header) return 0;
      const c = header.classList;
      return c.contains('shopify-section-header-sticky') && !c.contains('shopify-section-header-hidden') ? header.offsetHeight : 0;
    };
    const barH = () => { const b = bar(); return b && b.classList.contains('active') ? b.offsetHeight : 0; };
    const ease = () => {
      if (REDUCE.matches) return;
      // motion r1 (2026-10-22): the rail settles with the header, 380 ms, fast then soft (was 850 ms, slow start)
      rail.style.transition = 'top 0.38s cubic-bezier(0.22, 1, 0.36, 1)';
      clearTimeout(timer);
      timer = setTimeout(() => { rail.style.transition = ''; guardBar(); record(); }, 420);
    };
    const record = () => { recTop = rail.getBoundingClientRect().top; recY = scrollY; };
    const setRunway = v => {
      runway = Math.max(0, Math.round(v));
      const m = media(); if (m) m.style.setProperty('--mg-runway', runway + 'px');
    };
    // Add to Cart hidden under a revealed header (the theme counts it as on screen): show the bar
    const guardBar = () => {
      const b = bar(), cta = q(rail, '.product__buy-buttons .product-form__submit');
      if (!b || !cta) return;
      const hb = headerH() ? header.getBoundingClientRect().bottom : 0;
      const r = cta.getBoundingClientRect();
      const covered = hb > 0 && r.top < hb && r.bottom > 0;
      if (covered && !b.classList.contains('active')) { b.classList.add('active'); forcedBar = true; }
      else if (!covered && forcedBar) { forcedBar = false; if (r.bottom > 0 && r.top < innerHeight) b.classList.remove('active'); }
    };
    const update = force => {
      raf = 0;
      if (!DESK.matches) { rail.style.removeProperty('--mg-rail-top'); if (runway) setRunway(0); t = null; lastY = scrollY; return; }
      const y = scrollY, dy = y - lastY; lastY = y;
      const h = rail.offsetHeight;
      const hh = headerH();
      const maxT = hh + PAD;
      const minT = Math.min(maxT, innerHeight - barH() - PAD - h);
      if (t === null || force === true) t = Math.max(minT, Math.min(maxT, t === null ? maxT : t));
      else if (dy > 0) { if (t > minT) t = Math.max(minT, t - dy); }
      else if (dy < 0) t = Math.min(maxT, t - dy);
      const shown = hh > 0;
      if (shown !== hdrShown) {
        hdrShown = shown;
        ease();
        if (shown) {
          // the header slid in: a rail pinned at the top moves down with it; Add to Cart never ends up under it
          if (t >= PAD - 1) t = maxT;
          const cta = q(rail, '.product__buy-buttons');
          if (cta) t = Math.max(t, hh + 12 - (cta.getBoundingClientRect().top - rail.getBoundingClientRect().top));
        }
      }
      if (t > maxT) t = maxT;
      rail.style.setProperty('--mg-rail-top', Math.round(t) + 'px');
      guardBar();
      record();
    };
    const tick = () => { if (!raf) raf = requestAnimationFrame(() => update(false)); };
    addEventListener('scroll', tick, { passive: true });
    addEventListener('resize', () => { setRunway(0); update(true); }, { passive: true });
    if (header) new MutationObserver(tick).observe(header, { attributes: true, attributeFilter: ['class'] });
    // the theme's own observer may switch the bar back off while Add to Cart is still under the header
    new MutationObserver(ms => { if (DESK.matches && ms.some(m => m.target.classList && m.target.classList.contains('product-sticky-add-bar'))) guardBar(); })
      .observe(sec, { subtree: true, attributes: true, attributeFilter: ['class'] });
    if ('ResizeObserver' in window) new ResizeObserver(() => {
      if (!DESK.matches || recTop === null) return;
      if (Math.abs(scrollY - recY) < 1) {
        const d = recTop - rail.getBoundingClientRect().top; // > 0: the rail was pushed up by its own growth
        if (Math.abs(d) >= 1 && (d > 0 || runway > 0)) setRunway(runway + d);
      }
      record();
    }).observe(rail);
    update(true);
  }

  /* 2. Lead frame (desktop): show another media in the first tile without reflowing the grid. */
  function leadOf(sec) {
    const lead = items(sec)[0];
    if (!lead || lead.querySelector('.deferred-media, product-model')) return null;
    const img = q(lead, '.product__media-img img');
    return img ? { lead, img } : null;
  }
  function showInLead(sec, target) {
    const L = leadOf(sec);
    if (!L) return false;
    const { lead, img } = L;
    if (!lead.__mgOrig) {
      const tg0 = q(lead, '.product__media-toggle');
      lead.__mgOrig = { srcset: img.getAttribute('srcset'), src: img.getAttribute('src'), sizes: img.getAttribute('sizes'), alt: img.getAttribute('alt'), plate: lead.classList.contains('mg-media--plate'), video: lead.classList.contains('mg-media--video'), id: tg0 ? tg0.dataset.mediaId : null };
    }
    const o = lead.__mgOrig;
    let next;
    if (!target || target === lead) {
      next = { srcset: o.srcset, src: o.src, sizes: o.sizes, alt: o.alt, plate: o.plate, video: o.video, id: o.id };
    } else {
      const ti = q(target, '.product__media-img img') || q(target, '.deferred-media__poster img');
      if (!ti) return false;
      const fw = lead.clientWidth, fh = lead.clientHeight;
      const ar = (+ti.getAttribute('width') || ti.naturalWidth || 1) / (+ti.getAttribute('height') || ti.naturalHeight || 1);
      const over = fh ? Math.max(1, ar / (fw / fh)) : 1;
      next = { srcset: ti.getAttribute('srcset'), src: ti.getAttribute('src'), sizes: Math.ceil(fw * over) + 'px', alt: ti.getAttribute('alt'), plate: target.classList.contains('mg-media--plate'), video: target.classList.contains('mg-media--video'), id: mediaIdOf(target) };
    }
    if (img.getAttribute('src') === next.src) return true;
    const apply = () => {
      if (next.srcset) img.setAttribute('srcset', next.srcset); else img.removeAttribute('srcset');
      img.setAttribute('sizes', next.sizes || '');
      img.setAttribute('src', next.src);
      img.setAttribute('alt', next.alt || '');
      lead.classList.toggle('mg-media--plate', !!next.plate);
      lead.classList.toggle('mg-media--video', !!next.video);
      const tg = q(lead, '.product__media-toggle');
      if (tg && next.id) tg.dataset.mediaId = next.id;
    };
    if (REDUCE.matches) { apply(); return true; }
    // motion r1 (2026-10-22): a true crossfade. A copy of the picture on show stays on top while the new one decodes
    // underneath, then fades away (360 ms); the ring is never gone (it used to fade to the empty tray and back).
    const box = img.parentNode;
    const ib = img.getBoundingClientRect(), bb = box.getBoundingClientRect(), cs = getComputedStyle(img);
    const ghost = img.cloneNode(false);
    ghost.removeAttribute('id'); ghost.removeAttribute('loading'); ghost.setAttribute('alt', ''); ghost.setAttribute('aria-hidden', 'true');
    ghost.classList.add('mg-ghost');
    if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
    ghost.style.cssText = `position:absolute;left:${ib.left - bb.left}px;top:${ib.top - bb.top}px;width:${ib.width}px;height:${ib.height}px;max-width:none;margin:0;object-fit:${cs.objectFit};object-position:${cs.objectPosition};transform:${cs.transform};z-index:3;opacity:1;pointer-events:none;transition:opacity 0.36s cubic-bezier(0.4, 0, 0.2, 1);`;
    box.appendChild(ghost);
    apply();
    let gone = false;
    const fade = () => { if (gone) return; gone = true; requestAnimationFrame(() => { ghost.style.opacity = '0'; setTimeout(() => ghost.remove(), 420); }); };
    const ready = () => (img.decode ? img.decode() : Promise.resolve()).then(fade, fade);
    if (img.complete && img.naturalWidth) ready(); else { img.addEventListener('load', ready, { once: true }); img.addEventListener('error', fade, { once: true }); }
    setTimeout(fade, 2500);
    return true;
  }

  /* 3. Media bindings (re-run whenever the theme replaces .product__media). */
  function bindMedia(sec) {
    if (sec.__mgMo) sec.__mgMo.disconnect();
    const list = q(sec, '.product__media-list');
    const prog = q(sec, '.mg-gallery-progress');
    const cur = prog && q(prog, '.mg-gallery-progress__current');
    let last = -1;
    const progress = () => {
      if (!prog || !list) return;
      const s = list.swiper;
      const i = s ? (typeof s.realIndex === 'number' ? s.realIndex : s.activeIndex) : 0;
      if (i === last) return;
      last = i;
      prog.style.setProperty('--mg-index', i);
      if (cur) cur.textContent = i + 1;
    };
    // a variant's image: the theme marks its tile .active (desktop grid) and slides to it (phones)
    let active = null;
    const variantMedia = () => {
      if (!DESK.matches) return;
      const a = items(sec).find(it => it.classList.contains('active')) || null;
      if (a === active) return;
      active = a;
      if (!a) return;
      setPressed(sec, null);
      showInLead(sec, a);
    };
    if (list) {
      sec.__mgMo = new MutationObserver(() => { progress(); variantMedia(); });
      sec.__mgMo.observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    progress();
  }

  function setPressed(sec, btn) {
    sec.querySelectorAll('.mg-view__opt').forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
  }

  /* 4. One delegated click handler per section: View in, and the phone peek. */
  function onClick(sec, e) {
    const opt = e.target.closest('.mg-view__opt');
    if (opt && sec.contains(opt)) {
      const on = opt.getAttribute('aria-pressed') !== 'true';
      const target = items(sec).find(it => mediaIdOf(it) === opt.dataset.mgMediaId);
      if (!target) return;
      if (DESK.matches || !(q(sec, '.js-media-list') || {}).swiper) {
        setPressed(sec, on ? opt : null);
        if (!showInLead(sec, on ? target : null) && on) {
          target.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'center' });
        }
      } else {
        setPressed(sec, opt);
        const sw = q(sec, '.js-media-list').swiper;
        const idx = +(target.dataset.swiperSlideIndex || items(sec).indexOf(target));
        if (sw.params.loop) sw.slideToLoop(idx); else sw.slideTo(idx);
      }
    }
  }
  function onPeek(sec, e) {
    // capture phase: a tap on the half-visible next photo moves the gallery instead of zooming it
    if (DESK.matches) return;
    const slide = e.target.closest('.product__media-list .swiper-slide');
    if (!slide || !sec.contains(slide) || slide.classList.contains('swiper-slide-active')) return;
    const sw = slide.closest('.js-media-list') && slide.closest('.js-media-list').swiper;
    if (!sw || sw.allowClick === false) return;
    e.preventDefault();
    e.stopPropagation();
    const idx = +slide.dataset.swiperSlideIndex;
    if (sw.params.loop && !isNaN(idx)) sw.slideToLoop(idx); else sw.slideNext();
  }

  /* 3. pdp r7 (2026-10-22), below 990: the sticky Add to Cart bar shows whenever the rail's own button is off screen,
        above or below (the theme shows it only once the button has scrolled away), so the decision is at hand from
        the first screen. It still steps aside while the button or the footer's foot is on screen. */
  function initBar(sec) {
    const form = q(sec, '.product__buy-buttons > product-form');
    if (!form || !('IntersectionObserver' in window)) return;
    const foot = document.querySelector('.shopify-section-group-footer-group .footer-bottom');
    let formIn = true, footIn = false;
    const apply = () => {
      if (DESK.matches) return;
      const b = q(sec, '.product-sticky-add-bar');
      if (!b) return;
      const want = !formIn && !footIn;
      if (want !== b.classList.contains('active')) b.classList.toggle('active', want);
    };
    const io = new IntersectionObserver(es => {
      es.forEach(e => { if (e.target === form) formIn = e.isIntersecting; else footIn = e.isIntersecting; });
      apply();
    }, { threshold: 0.01 });
    io.observe(form);
    if (foot) io.observe(foot);
    // the theme's own observer switches the bar off while the button is below the window: put it back
    new MutationObserver(ms => { if (ms.some(m => m.target.classList && m.target.classList.contains('product-sticky-add-bar'))) apply(); })
      .observe(sec, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /* pdp r8 (2026-10-22). (a) A ring's Stage names the metal on show ("Shown in yellow gold"); the words follow the lead
     picture's alt, which showInLead() swaps with the metal. (b) The resting YOUR RING panel (990+, sticky at the
     window foot) gets a taller ivory fade while it is stuck, so the option rows pass under it instead of looking cut;
     in its own place it keeps the short fade. */
  function bindCaption(sec) {
    const cap = q(sec, '[data-mg-stage-metal]');
    const tile = cap && cap.closest('.product__media-item');
    const img = tile && q(tile, '.product__media-img img');
    if (!img || img.__mgCap) return;
    img.__mgCap = true;
    const set = () => {
      // the picture's alt names the metal ("Vivienne marquise engagement ring in rose gold, view 9"); no metal: no words
      const m = (img.getAttribute('alt') || '').toLowerCase().match(/\b(yellow gold|white gold|rose gold|platinum)\b/);
      cap.hidden = !m;
      if (m) cap.textContent = 'Shown in ' + m[1];
    };
    new MutationObserver(set).observe(img, { attributes: true, attributeFilter: ['alt'] });
  }
  function initRest(sec) {
    const tot = q(sec, '.mg-opts > .mg-opt__total');
    if (!tot || tot.__mgRest) return;
    tot.__mgRest = true;
    let raf = 0;
    const check = () => {
      raf = 0;
      const line = innerHeight - (parseFloat(getComputedStyle(tot).bottom) || 0);
      tot.classList.toggle('mg-total--stuck', DESK.matches && Math.abs(tot.getBoundingClientRect().bottom - line) < 2);
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(check); };
    addEventListener('scroll', on, { passive: true });
    addEventListener('resize', on);
    check();
  }

  function init(sec) {
    if (!sec || sec.__mgPdp) return; // one binding per section element, however often the script runs
    sec.__mgPdp = true;
    initRail(sec);
    initBar(sec);
    bindMedia(sec);
    bindCaption(sec);
    initRest(sec);
    const media = q(sec, '.product__media');
    if (media) new MutationObserver(() => { bindMedia(sec); bindCaption(sec); setPressed(sec, null); }).observe(media, { childList: true });
    sec.addEventListener('click', e => onClick(sec, e));
    sec.addEventListener('click', e => onPeek(sec, e), true);
    DESK.addEventListener('change', () => {
      setPressed(sec, null);
      const L = leadOf(sec);
      if (L && L.lead.__mgOrig) showInLead(sec, null);
    });
  }

  const boot = () => document.querySelectorAll('.product-section').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', e => { const s = e.target.querySelector('.product-section') || e.target.closest('.product-section'); if (s) init(s); });
})();
